// Utility functions for standup document parsing and timing

export const DEFAULT_WORDS_PER_MINUTE = 120;

// Remove stage directions ([...]) for timing/word counts
function stripStageDirections(str) {
  return str.replace(/\[[^\]]+\]/g, "");
}

// A bit header line looks like "== Bit Name" (name may be empty).
export const HEADER_LINE_REGEX = /^==\s*(.*)$/;

// Returns { name, prefixLength } for a raw header line, or null if the
// line isn't a bit header. prefixLength is how many raw characters
// ("==" plus any following spaces) precede the name text - useful for
// mapping a caret position in the displayed (marker-stripped) name back
// to a position in the raw line.
export function parseHeaderLine(line) {
  const m = line.match(HEADER_LINE_REGEX);
  if (!m) return null;
  return { name: m[1], prefixLength: line.length - m[1].length };
}

// Parse the body into bits based on lines starting with "=="
export function parseBits(body) {
  const lines = body.split("\n");
  const bits = [];
  let currentBit = { name: null, content: [] };
  let foundBit = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Allow empty titles
    const bitMatch = line.match(HEADER_LINE_REGEX);
    if (bitMatch) {
      if (currentBit.name !== null || currentBit.content.length) {
        bits.push(currentBit);
      }
      currentBit = { name: bitMatch[1].trim(), content: [] };
      foundBit = true;
    } else {
      currentBit.content.push(line);
    }
  }
  if (currentBit.name !== null || currentBit.content.length) {
    bits.push(currentBit);
  }

  // If no bits were found (no == lines), treat whole body as one bit
  if (!foundBit && body.trim().length > 0) {
    return [{ name: "", content: lines }];
  }

  return bits;
}

// Format seconds as m:ss
export function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Get total time string for a word count
export function getTotalTimeString(totalWords, wordsPerMinute = DEFAULT_WORDS_PER_MINUTE) {
  if (totalWords === 0) return "-";
  const totalSeconds = (totalWords / wordsPerMinute) * 60;
  return formatTime(Math.max(1, Math.round(totalSeconds)));
}

// Total performance time for a full document body, excluding stage directions
export function getTotalTimeForBody(body, wordsPerMinute = DEFAULT_WORDS_PER_MINUTE) {
  const totalWords = stripStageDirections(body).split(/\s+/).filter(Boolean).length;
  return getTotalTimeString(totalWords, wordsPerMinute);
}

const STAR_REGEX = /\*\s*([^\s*.()]+)(\.)?/g;
const PAREN_REGEX = /\(([^)]+)\)/g;
const BRACKET_REGEX = /\[([^\]]+)\]/g;

// Splits a raw line into tokens carrying both the raw text and the
// "clean" display text (markers stripped for punchlines, kept for stage
// directions - mirroring parseJokeLine in DocumentBody.js, which this
// intentionally parallels rather than reuses, so Perform mode/docx export
// stay untouched). Used by the WYSIWYG editor to render a line and to map
// between a caret's position in the displayed text and the raw string.
export function tokenizeLine(line) {
  const matches = [];
  let m;
  STAR_REGEX.lastIndex = 0;
  while ((m = STAR_REGEX.exec(line)) !== null) {
    matches.push({ kind: "star", index: m.index, length: m[0].length, word: m[1], dot: !!m[2] });
  }
  PAREN_REGEX.lastIndex = 0;
  while ((m = PAREN_REGEX.exec(line)) !== null) {
    matches.push({ kind: "paren", index: m.index, length: m[0].length, word: m[1] });
  }
  BRACKET_REGEX.lastIndex = 0;
  while ((m = BRACKET_REGEX.exec(line)) !== null) {
    matches.push({ kind: "stage", index: m.index, length: m[0].length, full: m[0] });
  }
  matches.sort((a, b) => a.index - b.index);

  const tokens = [];
  let lastIndex = 0;
  matches.forEach((mt) => {
    if (mt.index < lastIndex) return; // skip overlapping matches
    if (mt.index > lastIndex) {
      const raw = line.slice(lastIndex, mt.index);
      tokens.push({ type: "text", raw, display: raw });
    }
    if (mt.kind === "stage") {
      // Stage directions keep their brackets in the display text.
      tokens.push({ type: "stage", raw: mt.full, display: mt.full });
    } else if (mt.kind === "paren") {
      const raw = line.slice(mt.index, mt.index + mt.length);
      tokens.push({ type: "punchline", raw, display: mt.word, prefixLen: 1, marker: "paren" });
    } else {
      // star: the match may include a trailing "." which displays as its
      // own plain-text token, matching parseJokeLine's behavior.
      const dotLen = mt.dot ? 1 : 0;
      const raw = line.slice(mt.index, mt.index + mt.length - dotLen);
      tokens.push({
        type: "punchline",
        raw,
        display: mt.word,
        prefixLen: raw.length - mt.word.length,
        marker: "star",
      });
      if (mt.dot) tokens.push({ type: "text", raw: ".", display: "." });
    }
    lastIndex = mt.index + mt.length;
  });
  if (lastIndex < line.length) {
    const raw = line.slice(lastIndex);
    tokens.push({ type: "text", raw, display: raw });
  }
  return tokens.length ? tokens : [{ type: "text", raw: line, display: line }];
}

// Maps a caret offset in a line's displayed (marker-stripped) text back to
// the equivalent offset in its raw text. Header lines display just the
// name, so any offset maps by adding back the "== " prefix length.
export function displayOffsetToRawOffset(line, displayOffset) {
  const header = parseHeaderLine(line);
  if (header) return Math.min(line.length, displayOffset + header.prefixLength);

  let consumedDisplay = 0;
  let rawPos = 0;
  for (const t of tokenizeLine(line)) {
    const dLen = t.display.length;
    if (displayOffset <= consumedDisplay + dLen) {
      const within = displayOffset - consumedDisplay;
      return rawPos + (t.type === "punchline" ? (t.prefixLen || 0) + within : within);
    }
    consumedDisplay += dLen;
    rawPos += t.raw.length;
  }
  return line.length;
}

// The inverse of displayOffsetToRawOffset: maps a raw-text offset to the
// equivalent offset in the displayed (marker-stripped) text.
export function rawOffsetToDisplayOffset(line, rawOffset) {
  const header = parseHeaderLine(line);
  if (header) return Math.max(0, Math.min(header.name.length, rawOffset - header.prefixLength));

  let consumedDisplay = 0;
  let rawPos = 0;
  for (const t of tokenizeLine(line)) {
    const rLen = t.raw.length;
    if (rawOffset <= rawPos + rLen) {
      const within = rawOffset - rawPos;
      if (t.type === "punchline") {
        const withinDisplay = Math.max(0, Math.min(t.display.length, within - (t.prefixLen || 0)));
        return consumedDisplay + withinDisplay;
      }
      return consumedDisplay + within;
    }
    consumedDisplay += t.display.length;
    rawPos += rLen;
  }
  return consumedDisplay;
}

// Toggles markup of type `tokenType` ("punchline" or "stage") over a
// [start, end) raw-text selection:
// - If the selection overlaps ANY existing token of that type at all -
//   even just one character of it - every token it touches is stripped
//   of its marker entirely. You never have to select a punchline/stage
//   direction exactly; selecting part of a multi-word one, or a range
//   spanning several of them, removes all of what it touches.
// - Otherwise (the selection touches no existing token of that type),
//   the whole selection is wrapped as a single new one.
// Returns { line, caret } (the new raw line, and a raw-text offset to
// place the caret at afterward) or null if there's nothing to do.
function toggleLineMarkup(line, start, end, { tokenType, wrap, unwrapToken }) {
  if (start >= end) return null;

  let rawPos = 0;
  const spans = tokenizeLine(line).map((token) => {
    const span = { token, rawStart: rawPos, rawEnd: rawPos + token.raw.length };
    rawPos += token.raw.length;
    return span;
  });

  const overlapping = spans.filter(
    (s) => s.token.type === tokenType && s.rawStart < end && s.rawEnd > start
  );

  if (overlapping.length > 0) {
    let result = "";
    let cursor = 0;
    let caret = 0;
    overlapping.forEach((s) => {
      result += line.slice(cursor, s.rawStart);
      result += unwrapToken(s.token);
      caret = result.length;
      cursor = s.rawEnd;
    });
    result += line.slice(cursor);
    return { line: result, caret };
  }

  const selected = line.slice(start, end);
  if (!selected.trim()) return null;
  const replacement = wrap(selected);
  return { line: line.slice(0, start) + replacement + line.slice(end), caret: start + replacement.length };
}

// Wraps or unwraps punchline syntax ("*word" for a single word,
// "(phrase)" for multiple words) over a [start, end) raw-text selection.
// Used by the WYSIWYG editor's Punchline (Bold) button.
export function applyPunchlineToggle(line, start, end) {
  return toggleLineMarkup(line, start, end, {
    tokenType: "punchline",
    wrap: (selected) => (/\s/.test(selected) || /[*()]/.test(selected) ? `(${selected})` : `*${selected}`),
    unwrapToken: (token) => (token.marker === "paren" ? token.raw.slice(1, -1) : token.raw.slice(token.prefixLen || 0)),
  });
}

// Wraps or unwraps stage-direction syntax ("[stage direction]") over a
// [start, end) raw-text selection. Used by the WYSIWYG editor's Stage
// Direction button.
export function applyStageToggle(line, start, end) {
  return toggleLineMarkup(line, start, end, {
    tokenType: "stage",
    wrap: (selected) => `[${selected}]`,
    unwrapToken: (token) => token.raw.slice(1, -1),
  });
}

export function getBitTimings(bits, wordsPerMinute = DEFAULT_WORDS_PER_MINUTE) {
  let cumulativeSeconds = 0;
  const wordsPerSecond = wordsPerMinute / 60;

  return bits.map((bit) => {
    const text = Array.isArray(bit?.content) ? bit.content.join("\n") : "";
    const wordCount = stripStageDirections(text).split(/\s+/).filter(Boolean).length;
    const seconds = wordCount / wordsPerSecond;
    const start = cumulativeSeconds;
    const end = cumulativeSeconds + seconds;
    cumulativeSeconds = end;
    return { start, end };
  });
}